<script setup>
import { computed, h, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import ConversationRequiredAttributes from 'dashboard/components-next/ConversationWorkflow/ConversationRequiredAttributes.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
});

const store = useStore();
const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isSaving = ref(false);
const enabled = ref(false);
const inactivityMinutes = ref(60);
const durationUnit = ref(DURATION_UNITS.MINUTES);
const instructions = ref('');
const ignoreWaiting = ref(false);
const labelToApply = ref({});
const labels = useMapGetter('labels/getLabels');
const showRequiredAttributes = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_REQUIRED_ATTRIBUTES
  )
);

const labelOptions = computed(
  () =>
    labels.value?.map(label => ({
      id: label.title,
      name: label.title,
      icon: h('span', {
        class: 'size-[12px] rounded-sm ring-1 ring-inset ring-n-alpha-1',
        style: { backgroundColor: label.color },
      }),
    })) || []
);

const parsedMinutes = computed(() => {
  if (String(inactivityMinutes.value).trim() === '') return NaN;
  return Number(inactivityMinutes.value);
});
const isMinutesValid = computed(
  () =>
    Number.isInteger(parsedMinutes.value) &&
    parsedMinutes.value >= 10 &&
    parsedMinutes.value <= 10_080
);

const load = inbox => {
  const config = inbox?.auto_close_config || {};
  enabled.value = config.enabled === true || config.enabled === 'true';
  inactivityMinutes.value = config.inactivity_minutes ?? 60;
  instructions.value = config.instructions || '';
  ignoreWaiting.value =
    config.ignore_waiting === true || config.ignore_waiting === 'true';
  labelToApply.value =
    labelOptions.value.find(label => label.id === config.label) || {};

  if (inactivityMinutes.value % (24 * 60) === 0) {
    durationUnit.value = DURATION_UNITS.DAYS;
  } else if (inactivityMinutes.value % 60 === 0) {
    durationUnit.value = DURATION_UNITS.HOURS;
  } else {
    durationUnit.value = DURATION_UNITS.MINUTES;
  }
};

watch([() => props.inbox, labelOptions], () => load(props.inbox), {
  immediate: true,
});

const save = async () => {
  if (!isMinutesValid.value) return;
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      auto_close_config: {
        enabled: enabled.value,
        inactivity_minutes: inactivityMinutes.value,
        instructions: instructions.value.trim(),
        ignore_waiting: ignoreWaiting.value,
        label: labelToApply.value?.id || null,
        required_attributes:
          props.inbox?.auto_close_config?.required_attributes || [],
      },
    });
    useAlert(t('INBOX_ALERTS.AUTO_CLOSE_SAVED'));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('INBOX_SETTINGS.AUTO_CLOSE_SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="mx-auto max-w-4xl rounded-2xl border border-n-weak bg-n-surface-1 p-6"
  >
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ t('INBOX_SETTINGS.AUTO_CLOSE_TITLE') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('INBOX_SETTINGS.AUTO_CLOSE_DESCRIPTION') }}
      </p>
    </div>

    <label
      class="flex cursor-pointer items-start gap-3 rounded-xl border border-n-weak p-4"
    >
      <input v-model="enabled" type="checkbox" class="mt-1" />
      <span>
        <span class="block font-medium text-n-slate-12">{{
          t('INBOX_SETTINGS.AUTO_CLOSE_ENABLE')
        }}</span>
        <span class="mt-1 block text-sm text-n-slate-11">
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_OVERRIDE_HELP') }}
        </span>
      </span>
    </label>

    <div class="mt-5 grid gap-5">
      <div>
        <p
          id="auto-close-duration-label"
          class="mb-2 block text-sm font-medium text-n-slate-12"
        >
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_MINUTES_LABEL') }}
        </p>
        <div class="flex gap-2" aria-labelledby="auto-close-duration-label">
          <DurationInput
            v-model="inactivityMinutes"
            v-model:unit="durationUnit"
            :min="10"
            :max="10080"
            :disabled="!enabled"
          />
        </div>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_MINUTES_HELP') }}
        </p>
        <p
          v-if="enabled && !isMinutesValid"
          class="mt-1 text-sm text-n-ruby-10"
          role="alert"
        >
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_MINUTES_ERROR') }}
        </p>
      </div>

      <div>
        <label
          for="auto-close-instructions"
          class="mb-2 block text-sm font-medium text-n-slate-12"
        >
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_INSTRUCTIONS') }}
        </label>
        <textarea
          id="auto-close-instructions"
          v-model="instructions"
          rows="5"
          maxlength="1000"
          :disabled="!enabled"
          :placeholder="t('INBOX_SETTINGS.AUTO_CLOSE_INSTRUCTIONS_PLACEHOLDER')"
          class="w-full resize-y rounded-lg border border-n-weak bg-n-surface-2 px-3 py-2 text-n-slate-12 placeholder:text-n-slate-9 disabled:opacity-60"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('INBOX_SETTINGS.AUTO_CLOSE_INSTRUCTIONS_HELP') }}
        </p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-surface-2">
        <div
          class="flex items-center justify-between gap-4 border-b border-n-weak p-4"
        >
          <div>
            <p class="font-medium text-n-slate-12">
              {{ t('INBOX_SETTINGS.AUTO_CLOSE_PREFERENCES') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('INBOX_SETTINGS.AUTO_CLOSE_PREFERENCES_HELP') }}
            </p>
          </div>
        </div>
        <div class="grid gap-4 p-4 sm:grid-cols-2">
          <label
            class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-3"
          >
            <span class="text-sm text-n-slate-12">{{
              t('INBOX_SETTINGS.AUTO_CLOSE_IGNORE_WAITING')
            }}</span>
            <Switch v-model="ignoreWaiting" :disabled="!enabled" />
          </label>
          <div>
            <label class="mb-2 block text-sm font-medium text-n-slate-12">
              {{ t('INBOX_SETTINGS.AUTO_CLOSE_LABEL') }}
            </label>
            <SingleSelect
              v-if="enabled"
              v-model="labelToApply"
              :options="labelOptions"
              :placeholder="t('INBOX_SETTINGS.AUTO_CLOSE_LABEL_PLACEHOLDER')"
              placeholder-icon="i-lucide-chevron-down"
              placeholder-trailing-icon
              variant="faded"
            />
            <p v-else class="text-sm text-n-slate-10">
              {{
                labelToApply.name ||
                t('INBOX_SETTINGS.AUTO_CLOSE_LABEL_PLACEHOLDER')
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 rounded-xl bg-n-surface-2 p-4 text-sm text-n-slate-11">
      {{ t('INBOX_SETTINGS.AUTO_CLOSE_CHANNEL_NOTICE') }}
    </div>

    <div class="mt-6">
      <ConversationRequiredAttributes
        :inbox="inbox"
        :is-enabled="showRequiredAttributes"
      />
    </div>

    <div class="mt-6 flex justify-end">
      <NextButton
        :label="t('INBOX_SETTINGS.AUTO_CLOSE_SAVE')"
        :is-loading="isSaving"
        :disabled="isSaving || (enabled && !isMinutesValid)"
        @click="save"
      />
    </div>
  </div>
</template>
